import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { LoadingState } from '@/components/ui/States';
import type { Role } from '@/types/api';

import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  /** Optional list of roles allowed to view the route. If omitted, any
   *  authenticated user can access. */
  allow?: Role[];
  children?: ReactNode;
}

/**
 * Wraps a route segment and:
 *   1. Bounces to /login if not authed
 *   2. Shows "Not authorised" if the user lacks the required role
 *   3. Renders nested routes via <Outlet /> (or `children` if provided)
 */
export function ProtectedRoute({ allow, children }: ProtectedRouteProps) {
  const { status, me, hasRole } = useAuth();
  const loc = useLocation();

  if (status === 'idle' || status === 'loading') return <LoadingState />;

  if (status === 'unauthed' || !me) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }

  if (allow && !allow.some((r) => hasRole(r))) {
    return <NotAuthorised />;
  }

  return <>{children ?? <Outlet />}</>;
}

function NotAuthorised() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="text-3xl font-bold text-ink mb-2">Not authorised</div>
      <p className="max-w-md text-sm text-ink-soft">
        Your role doesn't have access to this page. Ask the shop owner to update your
        permissions in Settings → Employees.
      </p>
    </div>
  );
}

/** Conditionally render children only when the current user has any of `allow`. */
export function HasRole({ allow, children }: { allow: Role[]; children: ReactNode }) {
  const { hasRole } = useAuth();
  if (!allow.some((r) => hasRole(r))) return null;
  return <>{children}</>;
}
